import { ChangeDetectorRef, Component, inject, Injector, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize, map, tap } from 'rxjs/operators';
import { NgbDateAdapter, NgbDatepickerModule, NgbDropdownModule, NgbNavModule, NgbPopoverModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule, ListService, PagedResultDto, ReplaceableComponents } from '@abp/ng.core';
import {
  Confirmation,
  ConfirmationService,
  ToasterService,
  DateAdapter,
  ThemeSharedModule,
} from '@abp/ng.theme.shared';
import {
  ExtensibleModule,
  EXTENSIONS_IDENTIFIER,
  FormPropData,
  generateFormFromProps,
} from '@abp/ng.components/extensible';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import {
  EditionDto,
  EditionLookupDto,
  EditionService,
  GetTenantsInput,
  SaasTenantCreateDto,
  SaasTenantDto,
  tenantActivationStateOptions,
  TenantService,
} from '@volo/abp.ng.saas/proxy';
import { EditionsComponent, SetTenantPasswordModalInputs, SetTenantPasswordModalOutputs, TenantsComponent } from '@volo/abp.ng.saas';
import { eSaasComponents } from '@volo/abp.ng.saas';
import { ConnectionStringsComponent } from '@volo/abp.ng.saas';
import { TENANT_FORM_VALIDATORS_TOKEN } from '@volo/abp.ng.saas';
import { PageModule } from '@abp/ng.components/page';
import {  AdvancedEntityFiltersModule, CommercialUiModule } from '@volo/abp.commercial.ng.ui';
import { SaasModule } from '@volo/abp.ng.saas';
import { SaasRoutingModule } from '@volo/abp.ng.saas/lib/saas-routing.module';

interface SelectedModalContent {
  type: 'saveConnStr' | 'saveTenant';
  title: string;
  template: TemplateRef<any>;
}

@Component({
  selector: 'abp-tenants',
  templateUrl: './tenants.component.html',
  standalone: true,
  imports: [
    CoreModule,
    CommercialUiModule,
    ThemeSharedModule,
    NgbDropdownModule,
    PageModule,
    NgbNavModule,
    NgbPopoverModule,
    AdvancedEntityFiltersModule,
    NgbDatepickerModule,
    NgbTooltipModule,
    ExtensibleModule,
    FeatureManagementModule,
    SaasModule
  ],
  providers: [
    ListService,
    {
      provide: EXTENSIONS_IDENTIFIER,
      useValue: eSaasComponents.Tenants,
    },
    {
      provide: TenantsComponent,
      useExisting: CustomTenantsComponent
    },
    { provide: NgbDateAdapter, useClass: DateAdapter },
  ],
})
export class CustomTenantsComponent implements OnInit {
  protected readonly list = inject(ListService<GetTenantsInput>);
  protected readonly service = inject(TenantService);
  protected readonly confirmationService = inject(ConfirmationService);
  protected readonly fb = inject(UntypedFormBuilder);
  protected readonly injector = inject(Injector);
  protected readonly toasterService = inject(ToasterService);
  protected readonly editionService = inject(EditionService);
  protected readonly cdr = inject(ChangeDetectorRef);

  protected readonly tenantFormValidators = inject(TENANT_FORM_VALIDATORS_TOKEN);

  data: PagedResultDto<SaasTenantDto> = { items: [], totalCount: 0 };
  editions: EditionDto[];

  @ViewChild(ConnectionStringsComponent) connectionStringsRef: ConnectionStringsComponent;

  selected: SaasTenantDto;

  tenantForm: UntypedFormGroup;

  isModalVisible: boolean;

  showFilters: boolean = false;
  filterText: string;

  selectedModalContent = {} as SelectedModalContent;

  visibleFeatures = false;
  isVisibleSetTenantPasswordModal = false;
  selectedTenantId = '';
  selectedTenantNameForSetTenantPasswordModal = '';
  providerKey: string | null = null;

  modalBusy = false;

  emptyOption = { label: '-', value: null };

  connectionStringsReplacementKey = eSaasComponents.ConnectionStrings;
  setTenantPasswordReplacementKey = eSaasComponents.SetTenantPassword;
  formFilters: UntypedFormGroup;
  activationStateOptions = tenantActivationStateOptions;
  @ViewChild('tenantModalTemplate')
  tenantModalTemplate: TemplateRef<any>;

  @ViewChild('connectionStringModalTemplate')
  connectionStringModalTemplate: TemplateRef<any>;

  setTenantReplaceableTemplateOptions: ReplaceableComponents.ReplaceableTemplateDirectiveInput<
    SetTenantPasswordModalInputs,
    SetTenantPasswordModalOutputs
  > | null = null;

  get hasSelectedTenant(): boolean {
    return Boolean(this.selected.id);
  }

  get isDisabledSaveButton(): boolean {
    if (!this.selectedModalContent) {
      return false;
    }

    const { type } = this.selectedModalContent;
    const invalid = this.connectionStringsRef?.form.invalid;

    if (type === 'saveConnStr' && invalid) {
      return true;
    }

    return type === 'saveTenant' && this.tenantForm && this.tenantForm.invalid;
  }

  onVisibleFeaturesChange = (value: boolean) => {
    this.visibleFeatures = value;
    if (!value) {
      this.providerKey = null;
    }
  };

  closeSetTenantPasswordModal = () => {
    this.isVisibleSetTenantPasswordModal = false;
    this.selectedTenantNameForSetTenantPasswordModal = '';
    this.selectedTenantId = '';
    this.setTenantReplaceableTemplateOptions = null;
  };

  ngOnInit() {
    this.buildForm();
    this.hookToQuery();
  }

  clearFilters() {
    this.formFilters.reset({ times: {} });
  }

  protected hookToQuery() {
    this.list
      .hookToQuery(query => {
        const value = {
          ...this.formFilters.value,
          ...this.formFilters.value.times,
          filter: this.filterText,
        };
        return this.service.getList({ ...query, ...value });
      })
      .subscribe(res => (this.data = res));
  }

  protected createTenantForm() {
    return this.editionService.getList({ maxResultCount: 1000 }).pipe(
      tap(res => {
        this.editions = res.items;
        const data = new FormPropData(this.injector, this.selected);
        this.tenantForm = generateFormFromProps(data);

        if (this.tenantFormValidators) {
          this.tenantForm.addAsyncValidators(this.tenantFormValidators);
        }

        if (!data.record?.editionId) {
          this.tenantForm.controls.editionId.patchValue(null);
        }
      }),
    );
  }

  openModal(title: string, template: TemplateRef<any>, type: 'saveConnStr' | 'saveTenant') {
    this.selectedModalContent = {
      title,
      template,
      type,
    };

    this.isModalVisible = true;
  }

  onEditConnectionString(tenant: SaasTenantDto) {
    this.selected = tenant;

    this.openModal('Saas::ConnectionStrings', this.connectionStringModalTemplate, 'saveConnStr');
  }

  onAddTenant() {
    this.selected = {} as SaasTenantDto;
    this.createTenantForm().subscribe(() =>
      this.openModal('Saas::NewTenant', this.tenantModalTemplate, 'saveTenant'),
    );
  }

  onEditTenant(id: string) {
    this.service.get(id).subscribe(tenant => {
      this.selected = tenant;
      this.createTenantForm().subscribe(() =>
        this.openModal('Saas::Edit', this.tenantModalTemplate, 'saveTenant'),
      );
    });
  }

  save() {
    const { type } = this.selectedModalContent || {};

    if (type === 'saveTenant') {
      this.saveTenant();
      return;
    }

    if (type === 'saveConnStr') {
      this.saveConnectionString();
    }
  }

  saveConnectionString() {
    if (this.modalBusy) return;
    if (this.connectionStringsRef.form.invalid) {
      return;
    }

    this.service
      .updateConnectionStrings(this.selected.id, this.connectionStringsRef.getInput())
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => {
        this.isModalVisible = false;
        this.list.get();
        this.toasterService.success('AbpUi::SavedSuccessfully');
      });
  }

  saveTenant() {
    if (!this.tenantForm.valid || this.modalBusy) {
      return;
    }

    this.modalBusy = true;

    const value = {
      ...this.tenantForm.value,
      ...(!this.selected.id && { connectionStrings: this.connectionStringsRef.getInput() }),
    } as SaasTenantCreateDto;

    const { id } = this.selected;
    (id ? this.service.update(id, { ...this.selected, ...value }) : this.service.create(value))
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => {
        this.list.get();
        this.isModalVisible = false;
        this.toasterService.success('AbpUi::SavedSuccessfully');
      });
  }

  delete(id: string, name: string) {
    this.confirmationService
      .warn('Saas::TenantDeletionConfirmationMessage', 'AbpUi::AreYouSure', {
        messageLocalizationParams: [name],
      })
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.service.delete(id).subscribe(() => this.list.get());
          this.toasterService.success('AbpUi::DeletedSuccessfully', '', { life: 6000 });
        }
      });
  }

  onSharedDatabaseChange(value: boolean) {
    if (!value) {
      setTimeout(() => {
        const defaultConnectionString = document.getElementById(
          'defaultConnectionString',
        ) as HTMLInputElement;
        if (defaultConnectionString) {
          defaultConnectionString.focus();
        }
      }, 0);
    }
  }

  openFeaturesModal(providerKey: string) {
    this.visibleFeatures = false;
    this.providerKey = providerKey;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.visibleFeatures = true;
    });
  }

  openSetTenantPasswordModal(id: string, name: string) {
    this.isVisibleSetTenantPasswordModal = true;
    this.selectedTenantNameForSetTenantPasswordModal = name;
    this.selectedTenantId = id;

    this.setTenantReplaceableTemplateOptions = {
      outputs: { modalVisibleChange: this.closeSetTenantPasswordModal },
      inputs: {
        modalVisible: { value: this.isVisibleSetTenantPasswordModal },
        tenantId: { value: this.selectedTenantId },
        tenantName: { value: this.selectedTenantNameForSetTenantPasswordModal },
      },
      componentKey: this.setTenantPasswordReplacementKey,
    };
  }

  applyDatabaseMigrations(recordId: string) {
    this.service.applyDatabaseMigrations(recordId).subscribe(() =>
      this.toasterService.info('Saas::DatabaseMigrationQueuedAndWillBeApplied', '', {
        life: 6000,
      }),
    );
  }

  private buildForm() {
    this.formFilters = this.fb.group({
      getEditionNames: [null, []],
      editionId: [null, []],
      times: [{}],
      activationState: [null, []],
    });
  }

  getData() {
    if (this.formFilters.invalid && this.formFilters.touched) {
      return;
    }
    this.list.get();
  }

  getEditionLookup() {
    return () =>
      this.service
        .getEditionLookup()
        .pipe(map(items => ({ items }) as PagedResultDto<EditionLookupDto>));
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }
}
